import React, { useEffect, useState, memo } from "react";
import {
  Sun,
  Cloud,
  CloudRain,
  Zap,
  Wind,
  Thermometer,
  CloudSun,
  Umbrella,
} from "lucide-react";
import axios from "axios";

const EventWeather = memo(({ latitude, longitude, teeTime, date, onWeatherDataChange }) => {

  const [weather, setWeather] = useState({
    teeTimeTemp: null,
    maxTemp: null,
    cloudCover: null,
    precipitation: null,
    windSpeed: null,
  });

  useEffect(() => {
    const fetchWeather = async () => {
      try {
        if (!latitude || !longitude || !date || !teeTime) {
          return;
        }

        const formattedDate = new Date(date).toISOString().split("T")[0];
        const formattedTeeTime = `${formattedDate}T${teeTime}-05:00`; // Assume EST (GMT-5)
        const teeTimeDateTime = new Date(formattedTeeTime);

        if (isNaN(teeTimeDateTime)) {
          return;
        }

        const apiParams = {
          latitude,
          longitude,
          hourly: "temperature_2m,cloud_cover,precipitation_probability,wind_speed_10m,relative_humidity_2m,uv_index",
          start_date: formattedDate,
          end_date: formattedDate,
          temperature_unit: "fahrenheit",
          windspeed_unit: "mph",
          timezone: "auto",
        };

        // Fetch weather data
        const response = await axios.get("https://api.open-meteo.com/v1/forecast", {
          params: apiParams,
          withCredentials: false,
        });

        const hourlyData = response.data.hourly;
        const hourlyTimes = hourlyData.time.map((t) => new Date(t).getTime());

        // Find the closest time index
        const teeTimeIndex = hourlyTimes.findIndex((time) => {
          const diff = Math.abs(time - teeTimeDateTime.getTime());
          return diff < 3600000; // Within 1 hour
        });

        if (teeTimeIndex === -1) {
          return;
        }

        // Safe array access with bounds checking
        const teeTimeTemp = hourlyData.temperature_2m[teeTimeIndex];
        const maxTempIndex = Math.min(teeTimeIndex + 4, hourlyData.temperature_2m.length - 1);
        const maxTemp = hourlyData.temperature_2m[maxTempIndex];
        const windSpeed = hourlyData.wind_speed_10m[teeTimeIndex];
        const humidity = hourlyData.relative_humidity_2m?.[teeTimeIndex];
        const uvIndex = hourlyData.uv_index?.[teeTimeIndex];

        // Safe slice for cloud cover and precipitation probability
        const endIndex = Math.min(teeTimeIndex + 4, hourlyData.cloud_cover.length);
        const cloudSlice = hourlyData.cloud_cover.slice(teeTimeIndex, endIndex);
        const precipSlice = hourlyData.precipitation_probability.slice(teeTimeIndex, endIndex);
        const uvSlice = hourlyData.uv_index ? hourlyData.uv_index.slice(teeTimeIndex, endIndex) : [];

        const weatherData = {
          teeTimeTemp: teeTimeTemp != null ? teeTimeTemp.toFixed(0) : null,
          maxTemp: maxTemp != null ? maxTemp.toFixed(0) : null,
          laterTemp: maxTemp != null ? maxTemp.toFixed(0) : null, // 4 hours later temp
          cloudCover: cloudSlice.length > 0 ? Math.max(...cloudSlice.filter(x => x != null)).toFixed(0) : null,
          precipitation: precipSlice.length > 0 ? Math.max(...precipSlice.filter(x => x != null)).toFixed(0) : null,
          rainChance: precipSlice.length > 0 ? Math.max(...precipSlice.filter(x => x != null)).toFixed(0) : null, // Alias for email
          windSpeed: windSpeed != null ? windSpeed.toFixed(0) : null,
          humidity: humidity != null ? humidity.toFixed(0) : null,
          uvIndex: uvSlice.length > 0 ? Math.max(...uvSlice.filter(x => x != null)).toFixed(1) : null,
        };
        
        setWeather(weatherData);
        
        // Pass weather data to parent component for email functionality
        if (onWeatherDataChange) {
          onWeatherDataChange(weatherData);
        }
      } catch (error) {
        // Silently handle weather fetch error
      }
    };

    fetchWeather();
  }, [latitude, longitude, teeTime, date, onWeatherDataChange]);


  const getRainChanceIcon = (rainChance) => {
    if (rainChance < 10) return { icon: Sun, color: "#FFD700" }; // Bright gold for sunny
    if (rainChance < 30) return { icon: CloudSun, color: "#FFA500" }; // Orange for partly cloudy
    if (rainChance < 60) return { icon: Cloud, color: "#87CEEB" }; // Sky blue for cloudy
    if (rainChance < 80) return { icon: CloudRain, color: "#4682B4" }; // Steel blue for rain
    return { icon: Zap, color: "#483D8B" }; // Dark slate blue for thunderstorm
  };

  // Overall weather assessment for header icon
  const getOverallWeatherIcon = (weather) => {
    if (!weather) {
      return { icon: Sun, color: "#FFD700" }; // Default to sunny
    }
    
    const rainChance = weather.precipitation || 0;
    const windSpeed = weather.windSpeed || 0;
    const temp = weather.teeTimeTemp || 70;
    
    // High wind takes priority
    if (windSpeed > 20) {
      return { icon: Wind, color: "#DC2626" }; // Red for high wind
    }
    
    // Very hot or very cold conditions
    if (temp > 90 || temp < 45) {
      return { icon: Thermometer, color: temp > 90 ? "#DC2626" : "#2563EB" };
    }
    
    // High rain chance - umbrella weather
    if (rainChance > 60) {
      return { icon: Umbrella, color: "#4682B4" }; // Steel blue for umbrella needed
    }
    
    // Otherwise use rain chance progression
    return getRainChanceIcon(rainChance);
  };


  const getWindIcon = (windSpeed) => {
    const speed = windSpeed || 0;
    const color = speed > 20 ? "red" : speed > 10 ? "orange" : "green";
    return { icon: Wind, color };
  };

  const getTemperatureIcon = (temperature) => {
    const temp = temperature || 70; // Default to mild temperature
    if (temp < 45) {
      return { icon: Thermometer, color: "#1E90FF" }; // Bright Blue for Freezing
    } else if (temp < 60) {
      return { icon: Thermometer, color: "#4682B4" }; // Steel Blue for Cold
    } else if (temp < 70) {
      return { icon: Thermometer, color: "#FFA500" }; // Bright Orange for Mild
    } else if (temp < 90) {
      return { icon: Thermometer, color: "#FF4500" }; // Orange-Red for Warm
    } else {
      return { icon: Thermometer, color: "#FF0000" }; // Bright Red for Hot
    }
  };
  
  
  

  return (
    <div className="weather-card bg-white dark:bg-navy-700 shadow-md rounded-lg p-4 w-full max-w-md">
      {weather && weather.teeTimeTemp !== null ? (
        <div className="space-y-4">
          {/* Weather Header */}
          <div className="flex items-center justify-between border-b dark:border-gray-600 pb-2">
            <h4 className="text-base font-bold text-gray-800 dark:text-gray-100">Weather Forecast</h4>
            <div className="p-1 rounded-full bg-gray-50 dark:bg-navy-600 border border-gray-200 dark:border-gray-600">
              {React.createElement(getOverallWeatherIcon(weather).icon, {
                color: getOverallWeatherIcon(weather).color,
                size: 24,
                strokeWidth: 2
              })}
            </div>
          </div>

          {/* Weather Details Grid */}
          <div className="grid grid-cols-2 gap-4 dark:text-gray-200">
            {/* Left Column: Temperature */}
            <div className="space-y-2">
              <div className="flex items-center space-x-3">
                <div className="p-1 rounded bg-gray-50 dark:bg-navy-600">
                  {React.createElement(getTemperatureIcon(weather.teeTimeTemp).icon, {
                    color: getTemperatureIcon(weather.teeTimeTemp).color,
                    size: 20,
                    strokeWidth: 2
                  })}
                </div>
                <p>
                  <strong>Tee Time:</strong> {weather.teeTimeTemp || '--'}°F
                </p>
              </div>
              <div className="flex items-center space-x-3">
                <div className="p-1 rounded bg-gray-50 dark:bg-navy-600">
                  {React.createElement(getTemperatureIcon(weather.maxTemp || weather.teeTimeTemp).icon, {
                    color: getTemperatureIcon(weather.maxTemp || weather.teeTimeTemp).color,
                    size: 20,
                    strokeWidth: 2
                  })}
                </div>
                <p>
                  <strong>+4 Hrs Later:</strong> {weather.maxTemp || '--'}°F
                </p>
              </div>
            </div>

            {/* Right Column: Wind and Rain */}
            <div className="space-y-2">
              <div className="flex items-center space-x-3">
                <div className="p-1 rounded bg-gray-50 dark:bg-navy-600">
                  {React.createElement(getWindIcon(weather.windSpeed || 0).icon, {
                    color: getWindIcon(weather.windSpeed || 0).color,
                    size: 20,
                    strokeWidth: 2
                  })}
                </div>
                <p>
                  <strong>Wind:</strong> {weather.windSpeed || '--'} mph
                </p>
              </div>
              <div className="flex items-center space-x-3">
                <div className="p-1 rounded bg-gray-50 dark:bg-navy-600">
                  {React.createElement(getRainChanceIcon(weather.precipitation || 0).icon, {
                    color: getRainChanceIcon(weather.precipitation || 0).color,
                    size: 20,
                    strokeWidth: 2
                  })}
                </div>
                <p>
                  <strong>Rain Chance:</strong> {weather.precipitation || '--'}%
                </p>
              </div>
            </div>
          </div>
        </div>
      ) : (
        <p className="text-gray-500 dark:text-gray-400 text-center">Loading weather data...</p>
      )}
    </div>
  // <div className="weather-card bg-white shadow-md rounded-lg p-4 w-full max-w-md">

  //     {weather.teeTimeTemp !== null ? (
  //       <div className="space-y-4">
  //         <div className="flex items-center space-x-2">
  //           <FontAwesomeIcon icon={faThermometerHalf} className="text-blue-500" />
  //           <p>
  //             <strong>Tee Time Temp:</strong> {weather.teeTimeTemp}°F
  //           </p>
  //         </div>

  //         <div className="flex items-center space-x-2">
  //           <FontAwesomeIcon icon={faSun} className="text-yellow-500" />
  //           <p>
  //             <strong>Max Temp (5 hrs):</strong> {weather.maxTemp}°F
  //           </p>
  //         </div>

  //         <div className="flex items-center space-x-2">
  //           <FontAwesomeIcon
  //             icon={getCloudIcon(weather.cloudCover).icon}
  //             color={getCloudIcon(weather.cloudCover).color}
  //           />
  //           <p>
  //             <strong>Cloud Cover:</strong> {weather.cloudCover}%
  //           </p>
  //         </div>

  //         <div className="flex items-center space-x-2">
  //           <FontAwesomeIcon icon={faCloudRain} color="blue" />
  //           <p>
  //             <strong>Rain:</strong> {convertRainToInches(weather.precipitation)} inches
  //           </p>
  //         </div>

  //         <div className="flex items-center space-x-2">
  //           <FontAwesomeIcon icon={faWind} className="text-gray-600" />
  //           <p>
  //             <strong>Wind Speed:</strong> {weather.windSpeed} mph
  //           </p>
  //         </div>
  //       </div>
  //     ) : (
  //       <p className="text-gray-500 text-center">Loading weather data...</p>
  //     )}
  //   </div>
  );
});

EventWeather.displayName = 'EventWeather';

export default EventWeather;
